import { useQuery } from '@tanstack/react-query';
import { first, orderBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useKey, useList } from 'react-use';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import {
  VenueMapConfigPlaceholder,
  VenueMapUrl,
  VenueMapV3Url,
  VenueMapVersionPlaceholder,
} from 'src/utils/constants/constants';
import { SectionInfo } from 'src/WebApiController';

// Mirror section is defined closest angle but opposite direction
const getMirrorSection = (selected: SectionInfo, candidates: SectionInfo[]) => {
  return first(
    orderBy(
      candidates,
      (s) =>
        Math.abs((selected.directionalAngle ?? 0) + (s.directionalAngle ?? 0)),
      'asc'
    )
  );
};

// Control + M to add mirrors to selected sections
export const useCtrlPlusM = (
  markedSectionIds: number[],
  onToggleMirrors?: (sections: SectionInfo[], exclude?: boolean) => void
) => {
  const { venueMapInfo } = useEventMapContext();

  const [prevMarkedIds, setPrevMarkedIds] = useState<Set<number>>(new Set());
  const [mirrors, mirrorsMutation] = useList<number>();

  useEffect(() => {
    // Reset mirrors if marked sections changed not because of the mirrors
    if (!isEqual(prevMarkedIds, new Set(markedSectionIds))) {
      mirrorsMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(markedSectionIds)]);

  const zonedSections = useMemo(() => {
    return (venueMapInfo?.sections || []).reduce(
      (map, section) => {
        const id = section.rows.find((row) => !!row.tktClass)?.tktClass?.id;
        if (id) {
          (map[id] = map[id] || []).push(section);
        }
        return map;
      },
      {} as Record<string, SectionInfo[]>
    );
  }, [venueMapInfo?.sections]);

  const selectedSections = useMemo(
    () =>
      (venueMapInfo?.sections ?? []).filter(({ id }) =>
        markedSectionIds.includes(id)
      ),
    [markedSectionIds, venueMapInfo?.sections]
  );

  const onCtrlMClicked = useCallback(() => {
    if (!onToggleMirrors) {
      return;
    }
    // Toggle mirrors when mirrors are already marked
    if (
      mirrors.length > 0 &&
      mirrors.every((id) => markedSectionIds.includes(id))
    ) {
      const mirrorSections = (venueMapInfo?.sections ?? []).filter((s) =>
        mirrors.includes(s.id)
      );
      onToggleMirrors(mirrorSections, true);
      mirrorsMutation.reset();
      return;
    }

    const sectionIdsToAdd: Record<number, SectionInfo> = {};
    for (const selected of selectedSections) {
      if (!selected.directionalAngle) {
        continue;
      }
      const classId = selected.rows.find((row) => !!row.tktClass)?.tktClass?.id;
      if (classId && classId in zonedSections) {
        const sectionCandidates = (zonedSections[classId] || []).filter(
          ({ directionalAngle }) => directionalAngle
        );
        const mirror = getMirrorSection(selected, sectionCandidates);
        if (
          mirror &&
          !markedSectionIds.includes(mirror.id) &&
          !sectionIdsToAdd[mirror.id]
        ) {
          mirrorsMutation.push(mirror.id);
          sectionIdsToAdd[mirror.id] = mirror;
        }
      }
    }
    if (Object.keys(sectionIdsToAdd).length) {
      const idsToAdd = Array.from(Object.keys(sectionIdsToAdd));
      setPrevMarkedIds(new Set([...markedSectionIds, ...idsToAdd.map(Number)]));
      onToggleMirrors(Object.values(sectionIdsToAdd));
    } else {
      console.log('No mirrors found');
    }
  }, [
    markedSectionIds,
    mirrors,
    mirrorsMutation,
    onToggleMirrors,
    selectedSections,
    venueMapInfo?.sections,
    zonedSections,
  ]);

  useKey(
    (event: KeyboardEvent) => event.ctrlKey && event.key === 'm',
    onCtrlMClicked,
    { event: 'keydown' }
  );
};

export type VenueMapSvgFormat = 'v3' | 'v2';

export type VenueMapSvgUrls = {
  v3Url: string;
  v2Url: string;
};

/**
 * Builds the V3 and V2 SVG URLs for a venue config. The `?v=` query param is a
 * cache-buster and does not affect whether the asset exists, so both URLs share
 * the same version.
 */
export const buildVenueMapSvgUrls = (
  venueCfgId: number,
  svgMapVer?: number | null
): VenueMapSvgUrls => {
  const version = (svgMapVer ?? 1).toString();

  const build = (template: string) =>
    template
      .replace(VenueMapConfigPlaceholder, venueCfgId.toString())
      .replace(VenueMapVersionPlaceholder, version);

  return {
    v3Url: build(VenueMapV3Url),
    v2Url: build(VenueMapUrl),
  };
};

/**
 * Resolves which SVG version to render by probing the actual V3 asset with a
 * lightweight HEAD request. A 200 means the V3 asset exists and should be used;
 * anything else (404, network error) falls back to V2. This replaces the
 * geoJson-derived `hasRow` heuristic, which can be out of sync with the assets
 * actually published to the CDN (POS-9606).
 */
export const useVenueMapSvgFormat = (
  venueCfgId: number | undefined,
  svgMapVer: number | null | undefined,
  enabled: boolean
): VenueMapSvgUrls & { format: VenueMapSvgFormat; isLoading: boolean } => {
  const urls = useMemo(
    () =>
      venueCfgId != null
        ? buildVenueMapSvgUrls(venueCfgId, svgMapVer)
        : undefined,
    [venueCfgId, svgMapVer]
  );

  const v3Probe = useQuery({
    queryKey: ['venueMapSvgFormat', urls?.v3Url],
    queryFn: async () => {
      const response = await fetch(urls!.v3Url, { method: 'HEAD' });
      return response.ok;
    },
    enabled: enabled && urls != null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    v3Url: urls?.v3Url ?? '',
    v2Url: urls?.v2Url ?? '',
    format: v3Probe.data === true ? 'v3' : 'v2',
    isLoading: enabled && urls != null && v3Probe.isLoading,
  };
};
